import { compareDates, isValidLocalDate, limitOn, tallyDay } from '@tahi/core';
import { useState } from 'react';
import { useParams } from 'react-router';
import { removeSmokeLog, restoreSmokeLog, setDayTotal } from '../../../data/commands/quit';
import { useCommandEnv, useData } from '../../../data/hooks';
import * as repo from '../../../data/repos/quit';
import type { SmokeLogRow } from '../../../data/repos/quit';
import { useQuery } from '../../../data/useQuery';
import { Money, ScreenHeader } from '../../../ui';
import { showToast } from '../../../ui/toast';
import { DayTotalSheet } from '../components/DayTotalSheet';
import { copy, errorText, HISTORY_BACK } from '../copy';
import { countLine, dayLabel, timeOfDay } from '../format';
import { useQuitData, useQuitToday } from '../hooks';
import { CARD, FOCUS, HERO_NUM, PILL_BTN, SCREEN } from '../styles';

/** Which logs decide the day under spec §7 (the latest total and the taps after it). */
function countedIds(logs: readonly SmokeLogRow[]): Set<string> {
  const totals = logs
    .filter((l) => l.kind === 'day_total')
    .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  const latest = totals.at(-1);
  return new Set(
    logs
      .filter((l) => l === latest || (l.kind === 'tap' && (!latest || l.at > latest.at)))
      .map((l) => l.id),
  );
}

export function DayEditor() {
  const { date = '' } = useParams();
  const { userId } = useData();
  const env = useCommandEnv();
  const today = useQuitToday();
  const plan = useQuitData()?.plan ?? null;
  const { data: logs } = useQuery(['smoke_logs'], (db) => repo.listDayLogs(db, userId, date), [
    userId,
    date,
  ]);
  const [open, setOpen] = useState(false);

  if (!isValidLocalDate(date) || compareDates(date, today) > 0) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.historyTitle} back={HISTORY_BACK} />
        <p className="text-muted">{copy.invalidDay}</p>
      </main>
    );
  }

  const list = logs ?? [];
  const tally = tallyDay(list, date);
  const limit = plan ? limitOn(plan.taper, date) : null;
  const counted = countedIds(list);
  const label = dayLabel(date, today);
  const describe = (l: SmokeLogRow) =>
    l.kind === 'tap'
      ? copy.tapAt(timeOfDay(l.at, l.recordedTz))
      : copy.totalAt(l.count, timeOfDay(l.at, l.recordedTz));

  return (
    <main className={SCREEN}>
      <ScreenHeader title={label} back={HISTORY_BACK} />
      <section className={CARD}>
        <p className={HERO_NUM}>{countLine(tally.count, limit)}</p>
        {tally.costMinor > 0 ? (
          <Money minor={tally.costMinor} className="text-sm text-muted" />
        ) : null}
        <button type="button" className={`${PILL_BTN} mt-3`} onClick={() => setOpen(true)}>
          {copy.setTotal}
        </button>
      </section>

      {list.length === 0 ? (
        <p className="text-muted">{copy.noLogs}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-divider">
          {list.map((l) => {
            const text = describe(l);
            const replaced = !counted.has(l.id);
            return (
              <li
                key={l.id}
                aria-label={replaced ? `${text}, ${copy.replaced}` : text}
                className="flex min-h-11 items-center justify-between gap-3 py-2"
              >
                <span className={replaced ? 'text-muted line-through' : 'text-fg'}>
                  {text}
                  {replaced ? (
                    <span className="block text-xs no-underline">{copy.replaced}</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  aria-label={copy.deleteLog(text)}
                  className={`inline-flex min-h-11 items-center rounded-full px-3 text-sm text-destructive ${FOCUS}`}
                  onClick={async () => {
                    const r = await removeSmokeLog(env, l.id);
                    if (!r.ok) return showToast({ message: errorText(r.error) });
                    showToast({
                      message: copy.deleted,
                      action: { label: copy.undo, onPress: () => void restoreSmokeLog(env, l.id) },
                    });
                  }}
                >
                  {copy.deleteLog('').trim()}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <DayTotalSheet
        open={open}
        onOpenChange={setOpen}
        title={date === today ? copy.todaysTotal : copy.totalFor(label)}
        initial={tally.count}
        onSave={async (n) => {
          const r = await setDayTotal(env, date, n);
          return r.ok ? null : errorText(r.error);
        }}
      />
    </main>
  );
}
